import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Dashboard, AddBusiness, ExitToApp, 
  ChevronLeft, Menu, Refresh 
} from '@mui/icons-material';
import { 
  List, ListItem, ListItemIcon, ListItemText, 
  IconButton, Divider, styled, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Tooltip
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';

const SidebarWrapper = styled('div')(({ theme, open }) => ({
  width: open ? 240 : 64,
  height: '100vh',
  background: '#ffffff',
  position: 'fixed',
  zIndex: 100,
  boxShadow: '4px 0 20px rgba(0, 0, 0, 0.08)',
  transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
}));

const MenuButton = styled(IconButton)({
  margin: '12px',
  padding: '8px',
  color: '#2d3748',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    transform: 'scale(1.1)'
  },
});

const NavItem = styled(ListItem)(({ theme, active }) => ({
  minHeight: 56,
  padding: '8px 20px',
  margin: '4px 12px',
  borderRadius: 12,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  backgroundColor: active ? 'rgba(102, 126, 234, 0.1)' : 'transparent',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.08)',
    transform: 'translateX(4px)'
  },
  '& .MuiListItemIcon-root': {
    minWidth: 0,
    marginRight: '16px',
    color: active ? '#667eea' : '#4a5568',
    transition: 'color 0.2s ease'
  },
  '& .MuiListItemText-primary': {
    color: active ? '#667eea' : '#2d3748',
    fontWeight: active ? 600 : 500,
    fontSize: '0.875rem',
    letterSpacing: '0.025em'
  },
}));

const SuperAdminSidebar = ({ open, handleDrawerToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const handleLogoutClick = () => {
    setLogoutConfirmOpen(true);
  };

  const handleLogoutConfirm = () => {
    setLogoutConfirmOpen(false);
    logout();
    navigate('/login');
  };

  const navItems = [
    { 
      text: 'Dashboard', 
      icon: <Dashboard sx={{ fontSize: 22 }} />, 
      path: '/super-admin/dashboard' 
    },
    { 
      text: 'Create Organization', 
      icon: <AddBusiness sx={{ fontSize: 22 }} />, 
      path: '/super-admin/create-organization' 
    },
    { 
      text: 'Fetch Vendors', 
      icon: <Refresh sx={{ fontSize: 22 }} />, 
      path: '/super-admin/manage-vendors' 
    }
  ];

  return (
    <SidebarWrapper open={open}>
      {/* Logout Confirmation Dialog */}
      <Dialog
        open={logoutConfirmOpen}
        onClose={() => setLogoutConfirmOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: '#2d3748' }}>
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          Are you sure you want to log out of your account?
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={() => setLogoutConfirmOpen(false)}
            sx={{
              color: '#718096',
              '&:hover': { backgroundColor: 'rgba(113, 128, 150, 0.04)' }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutConfirm}
            variant="contained"
            sx={{
              backgroundColor: '#667eea',
              '&:hover': { backgroundColor: '#5a67d8' },
              borderRadius: '8px',
              textTransform: 'none',
              boxShadow: 'none'
            }}
          >
            Log Out
          </Button>
        </DialogActions>
      </Dialog>

      <MenuButton onClick={handleDrawerToggle}>
        {open ? <ChevronLeft /> : <Menu />}
      </MenuButton>

      <Divider sx={{ my: 1, mx: 2 }} />

      <List sx={{ flex: 1, px: 0 }}>
        {navItems.map((item) => (
          <Tooltip 
            key={item.text} 
            title={!open ? item.text : ''} 
            placement="right"
          >
            <NavItem
              button
              onClick={() => navigate(item.path)}
              active={isActive(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              {open && <ListItemText primary={item.text} />}
            </NavItem>
          </Tooltip>
        ))}
      </List>

      <Divider sx={{ mx: 2 }} />

      <Tooltip title={!open ? 'Logout' : ''} placement="right">
        <NavItem
          button
          onClick={handleLogoutClick}
          sx={{ mb: 2 }}
        >
          <ListItemIcon>
            <ExitToApp sx={{ fontSize: 22 }} />
          </ListItemIcon>
          {open && <ListItemText primary="Logout" />}
        </NavItem>
      </Tooltip>
    </SidebarWrapper>
  );
};

export default SuperAdminSidebar;